import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { useAccessToken } from '../hooks/useAuth';
import { getUserProfile, testConnection } from '../services/backendApiService';

const BackendContext = createContext();

/**
 * Context provider for backend API interactions
 */
export const BackendProvider = ({ children }) => {
    const { getAccessToken } = useAccessToken();
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('disconnected'); // 'disconnected', 'connecting', 'connected', 'error'

    /**
     * Test connection to backend
     */
    const testBackendConnection = useCallback(async () => {
        setLoading(true);
        setError(null);
        setConnectionStatus('connecting');
        
        try {
            // Get token specifically for the backend API with correct scopes
            const accessToken = await getAccessToken([process.env.REACT_APP_API_SCOPE || "api://a3889d33-ade4-48fa-839e-1b0fde939a33/access_as_user"]);
            console.log('Access token obtained for backend test with API scopes');
            
            const result = await testConnection(accessToken);
            setConnectionStatus('connected');
            console.log('Backend connection test successful:', result);
            return result;
        } catch (error) {
            console.error('Backend connection test failed:', error);
            setError(error.message);
            setConnectionStatus('error');
            throw error;
        } finally {
            setLoading(false);
        }
    }, [getAccessToken]);

    /**
     * Fetch user profile from backend
     */
    const fetchUserProfile = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Get token specifically for the backend API with correct scopes
            const accessToken = await getAccessToken([process.env.REACT_APP_API_SCOPE || "api://a3889d33-ade4-48fa-839e-1b0fde939a33/access_as_user"]);
            console.log('Access token obtained for user profile with API scopes');
            
            const profile = await getUserProfile(accessToken);
            setUserProfile(profile);
            setConnectionStatus('connected');
            console.log('User profile fetched successfully:', profile);
            return profile;
        } catch (error) {
            console.error('Failed to fetch user profile:', error);
            setError(error.message);
            setConnectionStatus('error');
            throw error;
        } finally {
            setLoading(false);
        }
    }, [getAccessToken]);

    /**
     * Make a generic authenticated request
     */
    const makeRequest = useCallback(async (requestFunction) => {
        setLoading(true);
        setError(null);
        
        try {
            // Get token specifically for the backend API with correct scopes
            const accessToken = await getAccessToken([process.env.REACT_APP_API_SCOPE || "api://a3889d33-ade4-48fa-839e-1b0fde939a33/access_as_user"]);
            const result = await requestFunction(accessToken);
            setConnectionStatus('connected');
            return result;
        } catch (error) {
            console.error('Request failed:', error);
            setError(error.message);
            setConnectionStatus('error');
            throw error;
        } finally {
            setLoading(false);
        }
    }, [getAccessToken]);

    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Reset all state
     */
    const reset = useCallback(() => {
        setUserProfile(null);
        setError(null);
        setLoading(false);
        setConnectionStatus('disconnected');
    }, []);

    const contextValue = useMemo(() => ({
        // State
        userProfile,
        loading,
        error,
        connectionStatus,
        
        // Actions
        testBackendConnection,
        fetchUserProfile,
        makeRequest,
        clearError,
        reset
    }), [
        userProfile,
        loading,
        error,
        connectionStatus,
        testBackendConnection,
        fetchUserProfile,
        makeRequest,
        clearError,
        reset
    ]);

    return (
        <BackendContext.Provider value={contextValue}>
            {children}
        </BackendContext.Provider>
    );
};

/**
 * Hook to use backend context
 */
export const useBackend = () => {
    const context = useContext(BackendContext);
    
    if (!context) {
        throw new Error('useBackend must be used within a BackendProvider');
    }
    
    return context;
};

export default BackendContext;
